import React from "react";
import { ShieldCheck, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export function InfoBadge({ title, text, testId = "info-badge" }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} onClick={(e) => e.stopPropagation()} aria-label={`What is ${title || "this"}?`}
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-slate-300 transition-colors hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300">
          <HelpCircle className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} onClick={(e) => e.stopPropagation()}
        className="w-72 rounded-xl border-slate-200 p-4 text-left shadow-lg" data-testid={`${testId}-content`}>
        {title && <p className="font-head text-sm font-bold text-slate-900">{title}</p>}
        <p className="mt-1 text-xs leading-relaxed text-slate-500">{text}</p>
      </PopoverContent>
    </Popover>
  );
}

export function Logo({ className = "", mark = true, light = false, stacked = false }) {
  const word = (
    <span className="font-head font-bold tracking-tight">
      <span className={light ? "text-white" : "text-[#0F172A]"}>Ser</span>
      <span className={light ? "text-indigo-300" : "text-[#4338CA]"}>vantage</span>
    </span>
  );
  if (stacked) {
    return (
      <div className={`flex flex-col items-center gap-3 ${className}`} data-testid="sv-logo">
        {mark && (
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#4338CA] text-white shadow-sm">
            <ShieldCheck className="h-8 w-8" strokeWidth={2.2} />
          </span>
        )}
        <span className="text-2xl">{word}</span>
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="sv-logo">
      {mark && (
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#4338CA] text-white">
          <ShieldCheck className="h-5 w-5" strokeWidth={2.2} />
        </span>
      )}
      <span className="text-xl">{word}</span>
    </div>
  );
}

export function IndustryCohort({ you, industry, label, testId = "industry-cohort" }) {
  const pct = (v) => Math.min(100, ((v || 0) / 5) * 100);
  const diff = you != null && industry != null ? Math.round((you - industry) * 100) / 100 : null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5">
          <Label>Industry Cohort{label ? ` · ${label}` : ""}</Label>
          <InfoBadge title="Industry Cohort" testId={`${testId}-info`}
            text="Compares your Service Index against the average of other businesses in the same industry, so you can see if you're ahead of or behind your peers." />
        </span>
        {diff != null && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${diff >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`} data-testid={`${testId}-diff`}>
            {diff >= 0 ? "+" : ""}{diff} vs peers
          </span>
        )}
      </div>
      {industry == null ? (
        <p className="mt-3 text-sm text-slate-400">Set your industry in Settings to see peer benchmarks.</p>
      ) : (
        <div className="mt-4 space-y-3">
          <Bar label="Your Service Index" value={you} pct={pct(you)} accent />
          <Bar label="Industry peer average" value={industry} pct={pct(industry)} />
        </div>
      )}
    </div>
  );
}

function Bar({ label, value, pct, accent }) {
  return (
    <div>
      <div className="mb-1 flex justify-between text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold text-slate-900">{value == null ? "—" : value}</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-slate-100">
        <div className={`h-2.5 rounded-full ${accent ? "bg-[#4338CA]" : "bg-slate-400"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}


export function Label({ children, className = "" }) {
  return (
    <span className={`text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 ${className}`}>
      {children}
    </span>
  );
}

const TREND_COLORS = { up: "#10B981", down: "#EF4444", flat: "#64748B" };

export function TrendPill({ value, suffix = "%", testId }) {
  const dir = value > 0 ? "up" : value < 0 ? "down" : "flat";
  const arrow = dir === "up" ? "↑" : dir === "down" ? "↓" : "→";
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold"
      style={{ color: TREND_COLORS[dir], background: dir === "up" ? "#ECFDF5" : dir === "down" ? "#FEF2F2" : "#F1F5F9" }}
    >
      {arrow} {Math.abs(value)}{suffix}
    </span>
  );
}

export function StatCard({ label, value, sub, accent = "#0F172A", testId, help, children, onClick }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      className={`rounded-xl border border-slate-200 bg-white p-5 transition-transform hover:-translate-y-0.5 sv-rise ${onClick ? "cursor-pointer hover:border-indigo-300" : ""}`}
    >
      <span className="flex items-center gap-1.5"><Label>{label}</Label>{help && <InfoBadge title={help.title} text={help.text} testId={`${testId}-info`} />}</span>
      <div className="mt-2 flex items-end justify-between gap-2">
        <span className="sv-metric text-3xl font-bold leading-none" style={{ color: accent }}>
          {value}
        </span>
        {children}
      </div>
      {sub && <p className="mt-2 text-sm text-slate-500">{sub}</p>}
    </div>
  );
}

export function scoreColor(ssi) {
  if (ssi >= 4.2) return "#10B981";
  if (ssi >= 3.5) return "#4338CA";
  if (ssi >= 3.0) return "#F59E0B";
  return "#EF4444";
}

export function ScoreRing({ value = 0, max = 5, size = 120, label = "Service Index", testId }) {
  const pct = Math.max(0, Math.min(1, value / max));
  const r = size / 2 - 10;
  const c = 2 * Math.PI * r;
  const col = scoreColor(value);
  return (
    <div className="relative inline-flex items-center justify-center" data-testid={testId} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8F0" strokeWidth="10" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth="10"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="sv-metric text-3xl font-bold" style={{ color: col }}>{value.toFixed(2)}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">/ {max}</span>
      </div>
    </div>
  );
}

export function Stars({ value, size = 18 }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} width={size} height={size} viewBox="0 0 24 24"
          fill={n <= value ? "#F59E0B" : "none"} stroke={n <= value ? "#F59E0B" : "#CBD5E1"} strokeWidth="1.8">
          <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 18l-6 3.4 1.4-6.8L2.3 9.1l6.9-.8z" strokeLinejoin="round" />
        </svg>
      ))}
    </span>
  );
}

const RISK_STYLES = {
  TRUSTED: ["#065F46", "#ECFDF5"],
  LOW_RISK: ["#1E40AF", "#EFF6FF"],
  REVIEW: ["#92400E", "#FEF3C7"],
  HIGH_RISK: ["#991B1B", "#FEE2E2"],
  REJECTED: ["#64748B", "#F1F5F9"],
};

export function RiskBadge({ status, testId }) {
  const [fg, bg] = RISK_STYLES[status] || RISK_STYLES.TRUSTED;
  return (
    <span data-testid={testId} className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold"
      style={{ color: fg, background: bg }}>
      {(status || "").replace("_", " ")}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
      {Icon && <Icon className="mb-3 h-10 w-10 text-slate-300" />}
      <p className="font-head text-lg font-semibold text-slate-700">{title}</p>
      {hint && <p className="mt-1 max-w-sm text-sm text-slate-500">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Avatar({ name, src, size = 40 }) {
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  if (src) {
    return <img src={src} alt={name} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-indigo-100 font-head font-bold text-indigo-700"
      style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </span>
  );
}
